import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { CbmImpl } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { checkReservationExists, getReservationsByTask, isEmailTask } from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { TaskParticipant } from "~/modules/participant";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

































export class RemoveEmailParticipant implements Action<Promise<TaskParticipant>> {
    readonly #taskSid: string;

    readonly #participantSid: string;

    constructor(taskSid: string, participantSid: string) {
        this.#taskSid = taskSid;
        this.#participantSid = participantSid;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const reservation = getReservationsByTask(worker, this.#taskSid)[0];

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "RemoveEmailParticipant",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.RemoveEmailParticipantCompleted,
            extra: { taskSid: this.#taskSid, participantSid: this.#participantSid },
            logger
        });
        const task = reservation.task;

        if (!isEmailTask(task)) {
            const errorMsg = `RemoveEmailParticipant: Task ${this.#taskSid} is not an email task`;
            sendWorkerTrackingEvent(EVENTS.RemoveEmailParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantSid: this.#participantSid
            });

            logger.error(errorMsg);
            return Promise.reject(
                createFlexSdkError(context)(ErrorCode.TaskNotEmailTask, { severity: ErrorSeverity.Error }, errorMsg)
            );
        }
        try {
            const participant = await cbm.removeEmailParticipant(task, this.#participantSid);
            sendWorkerTrackingEvent(EVENTS.RemoveEmailParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                participantSid: this.#participantSid
            });
            return participant;
        } catch (error) {
            const errorMsg = `RemoveEmailParticipant: Failed to remove email participant ${this.#participantSid}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.RemoveEmailParticipantCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid,
                participantSid: this.#participantSid
            });
            return Promise.reject(
                new FlexSdkError(
                    ErrorCode.FailedToRemoveEmailParticipant,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
